import type { ESize } from '@common/enums';
import type { CustomButtonEventPayload } from '@components/sp-custom-button';
import type { FormButtonSettingsFormType } from './sp-form-button-settings.type';
import type { ShadowSettings } from '@common/types/shadow-settings.type';

import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';

import { ERadioGroupType } from '@ui/sp-radio-group';
import { ButtonMode } from '@components/sp-custom-button';

import { EButtonStyleType, EButtonWidthType } from '@common/enums';

import { ThemeStateService } from '@sites/site-theme/src/state';

import type { FormButtonSettingsFormGroupType } from './sp-form-button-settings.type';
import { CSS_RESTRICTIONS } from '@common/constants';
import { isEmptyButtonLabel } from '@common/helpers/forms';
import type { ButtonAdditionalEffectsSettings } from '@components/sp-button-additional-effects-settings/type/button-additional-effects-settings.type';
import type { ButtonIcon } from '@components/sp-button-icon/type/button-icon.type';
import { ReactiveFormsModule } from '@angular/forms';
import { SpRadioGroupComponent } from '@ui/sp-radio-group/src/sp-radio-group.component';
import { NgIf } from '@angular/common';
import { SpCustomButtonComponent } from '@components/sp-custom-button/sp-custom-button.component';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpButtonIconComponent } from '@components/sp-button-icon/sp-button-icon.component';
import { SpButtonAdditionalEffectsSettingsComponent } from '../../sp-button-additional-effects-settings/sp-button-additional-effects-settings.component';
import { SpShadowSettingsComponent } from '../../sp-shadow-settings/sp-shadow-settings.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../../ui/sp-switch/src/sp-switch.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'sp-form-button-settings',
    templateUrl: './sp-form-button-settings.component.html',
    styleUrls: ['./sp-form-button-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        SpRadioGroupComponent,
        NgIf,
        SpCustomButtonComponent,
        SpInputNumComponent,
        SpButtonIconComponent,
        SpButtonAdditionalEffectsSettingsComponent,
        SpShadowSettingsComponent,
        SpSwitchComponent,
        TranslateModule,
    ],
})
export class SpFormButtonSettingsComponent {
    private readonly themeState = inject(ThemeStateService);

    @Input() public formButtonSettingsForm!: FormButtonSettingsFormGroupType;
    @Input() public isHorizontalForm?: boolean = false;

    public readonly CSS_RESTRICTIONS = CSS_RESTRICTIONS;

    public readonly EButtonWidthType = EButtonWidthType;
    public readonly EButtonStyleType = EButtonStyleType;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly ButtonMode = ButtonMode;

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public setSize(value: string): void {
        this.size.setValue(value as ESize);
    }

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        this.buttonBase.setValue(changes.base);
        this.buttonHover.setValue(changes.hover);

        if (changes?.isIndependentCorners !== undefined) {
            this.isIndependentCorners.setValue(changes.isIndependentCorners);
        }
        if (changes?.radiusSettings) {
            this.advancedRadius.setValue(changes.radiusSettings);
        }
        if (changes?.advancedBorderSettings) {
            this.advancedBorderSettings.setValue(changes.advancedBorderSettings);
        }
    }

    public get styleType(): FormButtonSettingsFormType['styleType'] {
        return this.formButtonSettingsForm.get('styleType') as FormButtonSettingsFormType['styleType'];
    }

    public get label(): FormButtonSettingsFormType['label'] {
        return this.formButtonSettingsForm.get('label') as FormButtonSettingsFormType['label'];
    }

    /** Blank button text is invalid — hosts gate Save on the same predicate. */
    public get isEmptyLabel(): boolean {
        return isEmptyButtonLabel(this.label?.value);
    }

    public get buttonBase(): FormButtonSettingsFormType['buttonBase'] {
        return this.formButtonSettingsForm.get('buttonBase') as FormButtonSettingsFormType['buttonBase'];
    }

    public get buttonHover(): FormButtonSettingsFormType['buttonHover'] {
        return this.formButtonSettingsForm.get('buttonHover') as FormButtonSettingsFormType['buttonHover'];
    }

    public get size(): FormButtonSettingsFormType['size'] {
        return this.formButtonSettingsForm.get('size') as FormButtonSettingsFormType['size'];
    }

    public get widthType(): FormButtonSettingsFormType['widthType'] {
        return this.formButtonSettingsForm.get('widthType') as FormButtonSettingsFormType['widthType'];
    }

    public get horizontalFormWidthType(): FormButtonSettingsFormType['horizontalFormWidthType'] {
        return this.formButtonSettingsForm.get(
            'horizontalFormWidthType'
        ) as FormButtonSettingsFormType['horizontalFormWidthType'];
    }

    public get widthValue(): FormButtonSettingsFormType['widthValue'] {
        return this.formButtonSettingsForm.get('widthValue') as FormButtonSettingsFormType['widthValue'];
    }

    public get buttonIcon(): FormButtonSettingsFormType['buttonIcon'] {
        return this.formButtonSettingsForm.get('buttonIcon') as FormButtonSettingsFormType['buttonIcon'];
    }

    public get additionalEffects(): FormButtonSettingsFormType['additionalEffects'] {
        return this.formButtonSettingsForm.get('additionalEffects') as FormButtonSettingsFormType['additionalEffects'];
    }

    public get isIndependentCorners(): FormButtonSettingsFormType['isIndependentCorners'] {
        return this.formButtonSettingsForm.get(
            'isIndependentCorners'
        ) as FormButtonSettingsFormType['isIndependentCorners'];
    }

    public get advancedRadius(): FormButtonSettingsFormType['advancedRadius'] {
        return this.formButtonSettingsForm.get('advancedRadius') as FormButtonSettingsFormType['advancedRadius'];
    }

    public get advancedBorderSettings(): FormButtonSettingsFormType['advancedBorderSettings'] {
        return this.formButtonSettingsForm.get(
            'advancedBorderSettings'
        ) as FormButtonSettingsFormType['advancedBorderSettings'];
    }

    public get isShadow(): FormButtonSettingsFormType['isShadow'] {
        return this.formButtonSettingsForm.get('isShadow') as FormButtonSettingsFormType['isShadow'];
    }

    public get shadowSettings(): FormButtonSettingsFormType['shadowSettings'] {
        return this.formButtonSettingsForm.get('shadowSettings') as FormButtonSettingsFormType['shadowSettings'];
    }

    public get shadowSettingsValue(): ShadowSettings {
        return this.shadowSettings?.value;
    }

    public buttonIconChangeHandler(changes: ButtonIcon): void {
        this.buttonIcon.setValue(changes);
    }

    public additionalEffectsChangeHandler(changes: ButtonAdditionalEffectsSettings): void {
        this.additionalEffects.setValue(changes);
    }

    public setShadow(value: boolean): void {
        this.isShadow.setValue(value);
    }

    public setShadowSettings(settings: ShadowSettings): void {
        this.shadowSettings.setValue(settings);
    }

    public setWidthValue(value: number): void {
        this.widthValue.setValue(value);
    }
}
